import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { supabase } from "../lib/supabase";
import { membershipPlans } from "../data/membershipPlans";
import {
  calculateCheckoutSummary,
  generateServiceDates,
  generateHalfHourSlots,
  getMaterialDeliveryEstimate,
} from "../utils/checkoutHelpers";
import { buildCheckoutIntelligence } from "../utils/checkoutIntelligence";
import {
  generateOrderCode,
  getOrderType,
} from "../utils/orderHelpers";
import {
  buildTypedAddressPayload,
  buildTypedOrderPayload,
  buildTypedPaidOrderUpdate,
} from "../utils/checkoutSupabasePayloads";
import { checkCityIsLive } from "../utils/cityAccess";
import { assignWorkerToBooking } from "../utils/assignmentEngine";
import { lockBookingMbCredits } from "../utils/rewardsEngine";

function Checkout() {
  const navigate = useNavigate();
  const { cartItems, membership, setMembership, clearMembership, clearCart } = useCart();
  const { isLoggedIn, profile, user } = useAuth();
  const razorpayKey = process.env.REACT_APP_RAZORPAY_KEY;
  const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
  const defaultRazorpayOrderUrl = supabaseUrl
    ? `${supabaseUrl.replace(".supabase.co", ".functions.supabase.co")}/create-razorpay-order`
    : "";
  const razorpayOrderUrl =
    process.env.REACT_APP_SUPABASE_RAZORPAY_ORDER_URL ||
    defaultRazorpayOrderUrl;

  const [selectedDate, setSelectedDate] = useState("");
  const [selectedSlot, setSelectedSlot] = useState("");
  const [priorityDelivery, setPriorityDelivery] = useState(false);
  const [placingOrder, setPlacingOrder] = useState(false);
  const [transportResponsibility, setTransportResponsibility] = useState("");
  const [projectBrief, setProjectBrief] = useState({
    budgetRange: "",
    urgency: "",
    plotSize: "",
    projectDetails: "",
    attachmentUrls: "",
  });

  const [selectedAddressType, setSelectedAddressType] = useState("");
  const [addressForm, setAddressForm] = useState({
    houseFlatNo: "",
    streetArea: "",
    city: "",
    state: "",
    country: "India",
    pincode: "",
  });

  const serviceItems = useMemo(
    () => cartItems.filter((item) => item.type === "service"),
    [cartItems]
  );

  const materialItems = useMemo(
    () => cartItems.filter((item) => item.type === "material"),
    [cartItems]
  );

  const serviceDates = useMemo(() => generateServiceDates(7), []);
  const serviceSlots = useMemo(() => generateHalfHourSlots(), []);
  const hasProjectBriefServices = useMemo(
    () => serviceItems.some((item) => item.requiresProjectBrief),
    [serviceItems]
  );

  const baseSummary = useMemo(
    () =>
      calculateCheckoutSummary({
        serviceItems,
        materialItems,
        membership,
        priorityDelivery,
      }),
    [serviceItems, materialItems, membership, priorityDelivery]
  );

  const labourCount = useMemo(
    () =>
      serviceItems.reduce(
        (sum, item) => sum + Math.max(1, Number(item.quantity || 1)),
        0
      ),
    [serviceItems]
  );

  const serviceDistanceKm = useMemo(() => {
    const distanceValues = serviceItems
      .map((item) => Number(item.distanceKm || 0))
      .filter((value) => Number.isFinite(value) && value > 0);

    if (!distanceValues.length) {
      return labourCount > 1 ? 6 : 4;
    }

    return Math.max(...distanceValues);
  }, [serviceItems, labourCount]);

  const checkoutIntelligence = useMemo(() => {
    const fallback = {
      transport: {
        quotedPrice: 0,
        operationalCost: 0,
        expectedMargin: 0,
        expectedMarginRate: 0,
        components: {},
      },
      mbCredits: {
        rate: 0,
        earnedCredits: 0,
        suppressedReward: false,
        displayLabel: "Rewards preview will appear after transport selection",
      },
      margin: {
        totalCustomerPayable: 0,
        platformRevenue: 0,
        transportMargin: 0,
        paymentGatewayCost: 0,
        fraudReserve: 0,
        contributionMargin: 0,
        contributionMarginRate: 0,
      },
    };

    if (!serviceItems.length) {
      return {
        ...fallback,
        mbCredits: {
          ...fallback.mbCredits,
          displayLabel: "Rewards preview applies to labour charges only",
        },
      };
    }

    return buildCheckoutIntelligence({
      labourSubtotal: baseSummary.serviceSubtotal,
      materialSubtotal: baseSummary.materialSubtotal,
      serviceFee: baseSummary.serviceFee + baseSummary.visitationFee,
      platformFee: baseSummary.platformFee,
      tax: baseSummary.tax,
      labourCount,
      distanceKm: transportResponsibility === "mistribazaar_arranged" ? serviceDistanceKm : 0,
      slotLabel: selectedSlot,
      routePoolingEligible:
        transportResponsibility === "mistribazaar_arranged" && labourCount > 1,
      pooledStops:
        transportResponsibility === "mistribazaar_arranged" && labourCount > 1 ? 2 : 1,
      supportCost: baseSummary.serviceSubtotal >= 5000 ? 40 : 25,
      opsOverhead: baseSummary.serviceSubtotal >= 5000 ? 60 : 25,
      customerTrustBand: "standard",
      fraudRiskBand: "low",
      customerLifecycleBand: "repeat",
      categoryMarginBand: "standard",
      peakBand: "normal",
    });
  }, [
    serviceItems,
    baseSummary,
    labourCount,
    serviceDistanceKm,
    selectedSlot,
    transportResponsibility,
  ]);

  const summary = useMemo(() => {
    const transportCharge =
      transportResponsibility === "mistribazaar_arranged"
        ? checkoutIntelligence.transport.quotedPrice
        : 0;

    return {
      ...baseSummary,
      transportCharge,
      mbCreditsRate: checkoutIntelligence.mbCredits.rate,
      mbCreditsEarned: checkoutIntelligence.mbCredits.earnedCredits,
      mbCreditsSuppressed: checkoutIntelligence.mbCredits.suppressedReward,
      mbCreditsLabel:
        transportResponsibility === "mistribazaar_arranged" || !serviceItems.length
          ? checkoutIntelligence.mbCredits.displayLabel
          : "Select transport to preview final route-aware rewards",
      contributionMargin: checkoutIntelligence.margin.contributionMargin,
      contributionMarginRate: checkoutIntelligence.margin.contributionMarginRate,
      total: baseSummary.total + transportCharge,
    };
  }, [baseSummary, checkoutIntelligence, serviceItems.length, transportResponsibility]);

  useEffect(() => {
    if (serviceDates.length > 0 && !selectedDate) {
      setSelectedDate(serviceDates[0].value);
    }

    if (serviceSlots.length > 0 && !selectedSlot) {
      setSelectedSlot(serviceSlots[0].value);
    }
  }, [serviceDates, serviceSlots, selectedDate, selectedSlot]);

  useEffect(() => {
    if (profile) {
      setAddressForm({
        houseFlatNo: profile.house_flat_no || profile.flat_house_no || "",
        streetArea: profile.street_area || "",
        city: profile.city || "",
        state: profile.state || "",
        country: profile.country || "India",
        pincode: profile.pincode || "",
      });
    }
  }, [profile]);

  const handleAddressChange = (field, value) => {
    setAddressForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleProjectBriefChange = (field, value) => {
    setProjectBrief((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const getResolvedAddress = () => {
    if (selectedAddressType === "profile") {
      return {
        houseFlatNo:
          profile?.house_flat_no || profile?.flat_house_no || addressForm.houseFlatNo,
        streetArea: profile?.street_area || addressForm.streetArea,
        city: profile?.city || addressForm.city,
        state: profile?.state || addressForm.state,
        country: profile?.country || addressForm.country,
        pincode: profile?.pincode || addressForm.pincode,
      };
    }

    return addressForm;
  };

  const validateCheckout = () => {
    if (!isLoggedIn || !user?.id) {
      alert("Please log in before placing an order.");
      navigate("/login");
      return false;
    }

    if (cartItems.length === 0) {
      alert("Your cart is empty.");
      return false;
    }

    if (serviceItems.length > 0 && (!selectedDate || !selectedSlot)) {
      alert("Please select service date and slot.");
      return false;
    }

    if (serviceItems.length > 0 && !transportResponsibility) {
      alert("Please confirm who will bear worker transport before placing the order.");
      return false;
    }

    if (!selectedAddressType) {
      alert("Please select whether you want to use your saved address or enter one manually.");
      return false;
    }

    const address = getResolvedAddress();

    if (
      !address.houseFlatNo ||
      !address.streetArea ||
      !address.city ||
      !address.state ||
      !address.country ||
      !address.pincode
    ) {
      alert("Please complete full address details.");
      return false;
    }

    return true;
  };

  const ensureCityIsLive = async () => {
    const address = getResolvedAddress();
    const addressCity = address.city?.trim();
    const live = await checkCityIsLive(addressCity);

    if (!live) {
      alert("MistriBazaar is not live in this city yet.");
      return false;
    }

    return true;
  };

  const loadRazorpay = () => {
    return new Promise((resolve) => {
      if (window.Razorpay) {
        resolve(true);
        return;
      }

      const script = document.createElement("script");
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const createCheckoutOrder = async (paymentMethod = "cash", paymentStatus = "pending") => {
    const orderCode = generateOrderCode();
    const resolvedAddress = getResolvedAddress();
    const resolvedAddressText = [
      resolvedAddress.houseFlatNo,
      resolvedAddress.streetArea,
      resolvedAddress.city,
      resolvedAddress.state,
      resolvedAddress.country,
      resolvedAddress.pincode,
    ]
      .filter(Boolean)
      .join(", ");

    const addressPayload = buildTypedAddressPayload({
      profile,
      addressForm,
      selectedAddressType,
      userId: user.id,
    });

    const { data: addressData, error: addressError } = await supabase
      .from("addresses")
      .insert(addressPayload)
      .select()
      .single();

    if (addressError) throw addressError;

    const orderPayload = buildTypedOrderPayload({
      userId: user.id,
      orderCode,
      orderType: getOrderType(serviceItems, materialItems),
      paymentStatus,
      paymentMethod,
      membership,
      summary,
      addressId: addressData.id,
      notes:
        serviceItems.length > 0
          ? `Order created from checkout flow. Worker transport: ${transportResponsibility}. Transport charge: ₹${summary.transportCharge || 0}. MB Credits preview: ${summary.mbCreditsEarned || 0}.`
          : "Order created from checkout flow",
    });

    let orderData = null;

    const { data: insertedOrderWithCustomerProfile, error: orderError } = await supabase
      .from("orders")
      .insert(orderPayload)
      .select()
      .single();

    if (orderError) {
      const missingColumnError =
        orderError.code === "PGRST204" ||
        /column/i.test(orderError.message || "");

      if (!missingColumnError) {
        throw orderError;
      }

      const {
        data: insertedLegacyOrder,
        error: legacyOrderError,
      } = await supabase
        .from("orders")
        .insert(orderPayload)
        .select()
        .single();

      if (legacyOrderError) throw legacyOrderError;
      orderData = insertedLegacyOrder;
    } else {
      orderData = insertedOrderWithCustomerProfile;
    }

    const orderItemsPayload = cartItems.map((item) => ({
      order_id: orderData.id,
      item_type: item.type,
      ref_id: item.materialId || item.serviceId || null,
      title: item.title,
      description: item.description || "",
      quantity: Number(item.quantity || 1),
      unit: item.unit || null,
      unit_price: Number(item.basePrice || item.finalPrice || 0),
      final_price: Number(item.finalPrice || 0),
      metadata: {
        warranty: item.warranty || null,
        shopName: item.shopName || null,
        imageUrl: item.imageUrl || null,
        serviceCategory: item.serviceCategory || null,
        duration: item.duration || null,
        transportResponsibility:
          item.type === "service" ? transportResponsibility : null,
        mbCreditsEarnPreview:
          item.type === "service" ? Number(summary.mbCreditsEarned || 0) : null,
      },
    }));

    if (summary.visitationFee > 0) {
      orderItemsPayload.push({
        order_id: orderData.id,
        item_type: "fee",
        ref_id: null,
        title: "Mandatory Visitation Fee",
        description:
          "Applied to service items priced below ₹300 at ₹99 per qualifying service quantity.",
        quantity: 1,
        unit: "charge",
        unit_price: Number(summary.visitationFee || 0),
        final_price: Number(summary.visitationFee || 0),
        metadata: {
          fee_type: "visitation",
        },
      });
    }

    if (summary.transportCharge > 0) {
      orderItemsPayload.push({
        order_id: orderData.id,
        item_type: "fee",
        ref_id: null,
        title: "MistriBazaar Arranged Transport",
        description:
          "Dynamic labour transport charge based on route, labour count, and timing.",
        quantity: 1,
        unit: "charge",
        unit_price: Number(summary.transportCharge || 0),
        final_price: Number(summary.transportCharge || 0),
        metadata: {
          fee_type: "transport",
          labour_count: labourCount,
          route_distance_km: serviceDistanceKm,
          transport_margin_rate: Number(
            checkoutIntelligence.transport.expectedMarginRate || 0
          ),
        },
      });
    }

    if (membership) {
      orderItemsPayload.push({
        order_id: orderData.id,
        item_type: "membership",
        ref_id: null,
        title: `Membership Plan - ${membership.name}`,
        description: `${membership.duration} plan with 10% cashback on eligible completed orders.`,
        quantity: 1,
        unit: "plan",
        unit_price: Number(summary.membershipPrice || 0),
        final_price: Number(summary.membershipPrice || 0),
        metadata: {
          membership_name: membership.name,
          cashback_rate: "10%",
        },
      });
    }

    if (orderItemsPayload.length > 0) {
      const { error: orderItemsError } = await supabase
        .from("order_items")
        .insert(orderItemsPayload);

      if (orderItemsError) throw orderItemsError;
    }

    if (serviceItems.length > 0) {
      const transportQuotePayload = {
        order_id: orderData.id,
        customer_user_id: user.id,
        city_code: resolvedAddress.city || null,
        labour_count: labourCount,
        distance_km: Number(serviceDistanceKm || 0),
        time_band: selectedSlot || null,
        weather_band: "clear",
        scarcity_band: "normal",
        access_friction_band: "standard",
        route_pooling_eligible:
          transportResponsibility === "mistribazaar_arranged" && labourCount > 1,
        pooled_stops:
          transportResponsibility === "mistribazaar_arranged" && labourCount > 1 ? 2 : 1,
        quoted_price: Number(summary.transportCharge || 0),
        estimated_partner_cost: Number(
          checkoutIntelligence.transport.operationalCost || 0
        ),
        expected_margin: Number(checkoutIntelligence.transport.expectedMargin || 0),
        expected_margin_rate: Number(
          checkoutIntelligence.transport.expectedMarginRate || 0
        ),
        quote_status:
          transportResponsibility === "mistribazaar_arranged"
            ? "accepted"
            : "cancelled",
        metadata: {
          transport_responsibility: transportResponsibility,
          pricing_components: checkoutIntelligence.transport.components || {},
        },
      };

      const marginSnapshotPayload = {
        order_id: orderData.id,
        customer_user_id: user.id,
        labour_subtotal: Number(summary.serviceSubtotal || 0),
        material_subtotal: Number(summary.materialSubtotal || 0),
        service_fee: Number(summary.serviceFee + summary.visitationFee || 0),
        platform_fee: Number(summary.platformFee || 0),
        transport_revenue: Number(summary.transportCharge || 0),
        transport_cost: Number(checkoutIntelligence.transport.operationalCost || 0),
        payment_gateway_cost: Number(
          checkoutIntelligence.margin.paymentGatewayCost || 0
        ),
        support_cost: baseSummary.serviceSubtotal >= 5000 ? 40 : 25,
        ops_overhead: baseSummary.serviceSubtotal >= 5000 ? 60 : 25,
        fraud_reserve: Number(checkoutIntelligence.margin.fraudReserve || 0),
        tax: Number(summary.tax || 0),
        mb_credit_cost: Number(
          summary.mbCreditsSuppressed ? 0 : summary.mbCreditsEarned || 0
        ),
        platform_revenue: Number(
          checkoutIntelligence.margin.platformRevenue || 0
        ),
        projected_contribution_margin: Number(
          checkoutIntelligence.margin.contributionMargin || 0
        ),
        projected_margin_rate: Number(
          checkoutIntelligence.margin.contributionMarginRate || 0
        ),
        margin_floor: 0.12,
        reward_suppressed: Boolean(summary.mbCreditsSuppressed),
        status: paymentStatus === "paid" ? "paid" : "quoted",
        metadata: {
          transport_responsibility: transportResponsibility,
          mb_credits_rate: summary.mbCreditsRate || 0,
          mb_credits_label: summary.mbCreditsLabel || null,
        },
      };

      await Promise.all([
        supabase.from("transport_quotes").insert(transportQuotePayload),
        supabase.from("checkout_margin_snapshots").insert(marginSnapshotPayload),
      ]).catch((snapshotError) => {
        console.warn("Checkout intelligence persistence skipped:", snapshotError);
      });
    }

    let createdServiceBookings = [];

    if (serviceItems.length > 0) {
      const serviceBookingPayload = serviceItems.map((item) => ({
        order_id: orderData.id,
        user_id: user.id,
        service_title: item.title,
        service_category: item.serviceCategory || "general",
        service_slug:
          (item.title || "")
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, "-")
            .replace(/^-|-$/g, "") || null,
        booking_mode: item.bookingMode || null,
        booking_date: selectedDate,
        booking_slot: selectedSlot,
        city: resolvedAddress.city || null,
        address_text: resolvedAddressText || null,
        transport_responsibility: transportResponsibility || null,
        customer_name: profile?.full_name || profile?.name || null,
        customer_phone: profile?.phone || null,
        budget_range: projectBrief.budgetRange || null,
        urgency: projectBrief.urgency || null,
        plot_size: projectBrief.plotSize || null,
        project_details: projectBrief.projectDetails || null,
        attachment_urls: projectBrief.attachmentUrls
          ? projectBrief.attachmentUrls
              .split(",")
              .map((itemValue) => itemValue.trim())
              .filter(Boolean)
          : [],
        status: "pending_assignment",
        rate: Number(item.finalPrice || 0),
        notes:
          `${item.description || ""}${
            item.description ? " " : ""
          }Transport responsibility: ${transportResponsibility}.`.trim(),
      }));

      const { data: insertedWithLaunchFields, error: serviceError } = await supabase
        .from("service_bookings")
        .insert(serviceBookingPayload)
        .select();

      if (serviceError) {
        const missingColumnError =
          serviceError.code === "PGRST204" ||
          /column/i.test(serviceError.message || "");

        if (!missingColumnError) {
          throw serviceError;
        }

        const legacyServiceBookingPayload = serviceBookingPayload.map(
          ({
            city,
            address_text,
            transport_responsibility,
            service_slug,
            booking_mode,
            customer_name,
            customer_phone,
            budget_range,
            urgency,
            plot_size,
            project_details,
            attachment_urls,
            ...legacyPayload
          }) => legacyPayload
        );

        const {
          data: insertedLegacyRows,
          error: legacyServiceError,
        } = await supabase
          .from("service_bookings")
          .insert(legacyServiceBookingPayload)
          .select();

        if (legacyServiceError) throw legacyServiceError;
        createdServiceBookings = insertedLegacyRows || [];
      } else {
        createdServiceBookings = insertedWithLaunchFields || [];
      }

      if (createdServiceBookings?.length) {
        await Promise.all(
          createdServiceBookings.map((booking) => assignWorkerToBooking(booking))
        );
      }
    }

    if (materialItems.length > 0) {
      const materialOrdersPayload = materialItems.map((item) => ({
        order_id: orderData.id,
        user_id: user.id,
        material_title: item.title,
        shop_id: item.shopId || null,
        delivery_city: resolvedAddress.city || null,
        delivery_address_text: resolvedAddressText || null,
        quantity: Number(item.quantity || 1),
        unit: item.unit || "unit",
        unit_price: Number(item.basePrice || item.finalPrice || 0),
        final_price: Number(item.finalPrice || 0),
        priority_delivery: priorityDelivery,
        delivery_estimate: getMaterialDeliveryEstimate({
          quantity: item.quantity || 1,
          priorityDelivery,
          distanceKm: item.distanceKm || 5,
          vehicleType: item.vehicleType || "bike",
        }),
        delivery_status: "awaiting_shop_confirmation",
        assigned_delivery_partner: null,
        tracking_id: null,
      }));

      const { error: materialError } = await supabase
        .from("material_orders")
        .insert(materialOrdersPayload);

      if (materialError) {
        const missingColumnError =
          materialError.code === "PGRST204" ||
          /column/i.test(materialError.message || "");

        if (!missingColumnError) {
          throw materialError;
        }

        const legacyMaterialOrdersPayload = materialOrdersPayload.map(
          ({ delivery_city, delivery_address_text, ...legacyPayload }) =>
            legacyPayload
        );

        const { error: legacyMaterialError } = await supabase
          .from("material_orders")
          .insert(legacyMaterialOrdersPayload);

        if (legacyMaterialError) throw legacyMaterialError;
      }
    }

    return { orderData, orderCode, createdServiceBookings };
  };

  const handleSuccessfulOrder = (paymentMode, orderCode) => {
    clearCart();
    alert(`Order placed successfully via ${paymentMode}. Order ID: ${orderCode}`);
    navigate("/my-bookings");
  };

  const savePayment = async (paymentResponse, orderId) => {
    const { error } = await supabase.from("payments").insert({
      order_id: orderId,
      order_type: getOrderType(serviceItems, materialItems),
      user_id: user.id,
      razorpay_order_id: paymentResponse.razorpay_order_id,
      razorpay_payment_id: paymentResponse.razorpay_payment_id,
      razorpay_signature: paymentResponse.razorpay_signature,
      amount: summary.total,
      payment_mode: "online",
      status: "paid",
    });

    if (error) throw error;
  };

  const handleCashPayment = async () => {
    if (!validateCheckout()) return;

    try {
      setPlacingOrder(true);
      const live = await ensureCityIsLive();
      if (!live) return;

      const { orderData, orderCode } = await createCheckoutOrder("cash", "pending");

      const { error } = await supabase.from("payments").insert({
        order_id: orderData.id,
        order_type: getOrderType(serviceItems, materialItems),
        user_id: user.id,
        amount: summary.total,
        payment_mode: "cash",
        status: "created",
      });

      if (error) throw error;

      handleSuccessfulOrder("cash", orderCode);
    } catch (error) {
      console.error("PLACE ORDER ERROR:", error);
      alert(error.message || "Failed to place order.");
    } finally {
      setPlacingOrder(false);
    }
  };

  const handleOnlinePayment = async () => {
    if (!validateCheckout()) return;

    if (!razorpayKey || razorpayKey === "YOUR_RAZORPAY_KEY") {
      alert("Razorpay key is missing. Add REACT_APP_RAZORPAY_KEY to continue.");
      return;
    }

    if (!razorpayOrderUrl) {
      alert("Razorpay order URL is missing. Configure Supabase project environment first.");
      return;
    }

    try {
      setPlacingOrder(true);
      const live = await ensureCityIsLive();
      if (!live) return;

      const { orderData, orderCode, createdServiceBookings } = await createCheckoutOrder("online", "pending");

      const res = await loadRazorpay();

      if (!res) {
        alert("Failed to load Razorpay");
        return;
      }

      const {
        data: { session },
      } = await supabase.auth.getSession();

      if (!session?.access_token) {
        throw new Error("Please log in again to continue with online payment.");
      }

      const response = await fetch(razorpayOrderUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ amount: summary.total, orderId: orderData.id }),
      });

      if (!response.ok) {
        throw new Error("Failed to create Razorpay order.");
      }

      const data = await response.json();

      const options = {
        key: razorpayKey,
        amount: data.amount,
        currency: data.currency,
        name: "MistriBazaar",
        description: "Service Payment",
        order_id: data.id,
        handler: async function (paymentResponse) {
          try {
            await savePayment(paymentResponse, orderData.id);

            const { error } = await supabase
              .from("orders")
              .update(buildTypedPaidOrderUpdate())
              .eq("id", orderData.id);

            if (error) throw error;

            if (
              user?.id &&
              serviceItems.length > 0 &&
              !summary.mbCreditsSuppressed &&
              Number(summary.mbCreditsEarned || 0) > 0 &&
              createdServiceBookings?.length
            ) {
              const totalEligibleLabour = createdServiceBookings.reduce(
                (sum, booking) => sum + Number(booking.rate || 0),
                0
              );

              await Promise.all(
                createdServiceBookings.map((booking, index) => {
                  const bookingRate = Number(booking.rate || 0);
                  const proportionalCredits =
                    index === createdServiceBookings.length - 1
                      ? Math.max(
                          0,
                          Number(summary.mbCreditsEarned || 0) -
                            createdServiceBookings
                              .slice(0, index)
                              .reduce((sum, priorBooking) => {
                                const priorRate = Number(priorBooking.rate || 0);
                                const priorShare =
                                  totalEligibleLabour > 0
                                    ? Math.round(
                                        (priorRate / totalEligibleLabour) *
                                          Number(summary.mbCreditsEarned || 0)
                                      )
                                    : 0;
                                return sum + priorShare;
                              }, 0)
                        )
                      : totalEligibleLabour > 0
                        ? Math.round(
                            (bookingRate / totalEligibleLabour) *
                              Number(summary.mbCreditsEarned || 0)
                          )
                        : 0;

                  return lockBookingMbCredits({
                    userId: user.id,
                    orderId: orderData.id,
                    serviceBookingId: booking.id,
                    eligibleLabourSubtotal: bookingRate,
                    earnRate: Number(summary.mbCreditsRate || 0),
                    creditAmount: proportionalCredits,
                    metadata: {
                      source: "checkout_online_payment",
                      transport_responsibility: transportResponsibility,
                      order_code: orderCode,
                    },
                  });
                })
              ).catch((rewardError) => {
                console.warn("MB Credits lock skipped:", rewardError);
              });
            }

            handleSuccessfulOrder("online", orderCode);
          } catch (saveError) {
            console.error("PAYMENT SAVE ERROR:", saveError);
            alert(saveError.message || "Payment captured, but saving failed.");
          } finally {
            setPlacingOrder(false);
          }
        },
        prefill: {
          name: profile?.full_name || profile?.name || "Customer",
        },
        theme: {
          color: "#000000",
        },
        modal: {
          ondismiss: function () {
            setPlacingOrder(false);
          },
        },
      };

      const paymentObject = new window.Razorpay(options);
      paymentObject.open();
    } catch (error) {
      console.error("ONLINE PAYMENT ERROR:", error);
      alert(error.message || "Failed to start online payment.");
      setPlacingOrder(false);
    }
  };

  const renderSlotStatus = (status) => {
    if (status === "available") return "Available";
    if (status === "filling_fast") return "Filling fast";
    return "Not available";
  };

  return (
    <div style={page}>
      <div style={container} className="checkout-page__container">
        <button onClick={() => navigate("/cart")} style={backBtn} className="checkout-page__back-button">
          ← Back to Cart
        </button>

        <div style={heroCard} className="checkout-page__hero">
          <div>
            <h1 style={heroTitle} className="checkout-page__title">Checkout</h1>
            <p style={heroText} className="checkout-page__hero-text">
              Confirm your service schedule, delivery estimate, address, and final
              price.
            </p>
          </div>

          <div style={heroTotalCard} className="checkout-page__hero-total">
            <div style={heroTotalLabel}>Total Payable</div>
            <div style={heroTotalValue}>₹{summary.total}</div>
          </div>
        </div>

        <div style={layout} className="checkout-page__layout">
          <div style={leftColumn}>
            {serviceItems.length > 0 && (
              <div style={sectionCard}>
                <h2 style={sectionTitle}>Service Scheduling</h2>
                <p style={sectionText}>
                  Choose your service date and time slot. Slots are shown every 30
                  minutes.
                </p>

                <label style={label}>Select Date</label>
                <div style={chipWrap} className="checkout-page__chip-wrap">
                  {serviceDates.map((dateItem) => (
                    <button
                      key={dateItem.value}
                      onClick={() => setSelectedDate(dateItem.value)}
                      style={{
                        ...chip,
                        background: selectedDate === dateItem.value ? "#fff" : "#111",
                        color: selectedDate === dateItem.value ? "#000" : "#fff",
                      }}
                    >
                      {dateItem.label}
                    </button>
                  ))}
                </div>

                <label style={{ ...label, marginTop: "20px" }}>Select Slot</label>
                <div style={slotGrid} className="checkout-page__slot-grid">
                  {serviceSlots.map((slot) => (
                    <button
                      key={slot.value}
                      onClick={() => setSelectedSlot(slot.value)}
                      style={{
                        ...slotCard,
                        background:
                          selectedSlot === slot.value ? "#ffffff" : "#101010",
                        color:
                          selectedSlot === slot.value ? "#000000" : "#ffffff",
                        border:
                          selectedSlot === slot.value
                            ? "1px solid #fff"
                            : "1px solid #2a2a2a",
                      }}
                    >
                      <div style={{ fontWeight: "bold" }}>{slot.label}</div>
                      <div style={slotStatus(slot.status)}>
                        {renderSlotStatus(slot.status)}
                      </div>
                    </button>
                  ))}
                </div>

                <label style={{ ...label, marginTop: "20px" }}>
                  Worker Transport
                </label>
                <p style={sectionText}>
                  Labour and site workers often do not have their own transport.
                  Confirm whether you will bear transport locally or want
                  MistriBazaar to arrange transport to your location.
                </p>
                <div style={chipWrap} className="checkout-page__chip-wrap">
                  <button
                    type="button"
                    onClick={() => setTransportResponsibility("customer_arranged")}
                    style={{
                      ...transportChip,
                      background:
                        transportResponsibility === "customer_arranged"
                          ? "#fff"
                          : "#111",
                      color:
                        transportResponsibility === "customer_arranged"
                          ? "#000"
                          : "#fff",
                    }}
                  >
                    I will bear transport
                  </button>
                  <button
                    type="button"
                    onClick={() => setTransportResponsibility("mistribazaar_arranged")}
                    style={{
                      ...transportChip,
                      background:
                        transportResponsibility === "mistribazaar_arranged"
                          ? "#fff"
                          : "#111",
                      color:
                        transportResponsibility === "mistribazaar_arranged"
                          ? "#000"
                          : "#fff",
                    }}
                  >
                    Arrange transport for me
                  </button>
                </div>
                {!transportResponsibility ? (
                  <p style={warningText}>
                    This selection is mandatory before placing a service order.
                  </p>
                ) : null}
                {transportResponsibility === "mistribazaar_arranged" ? (
                  <div style={membershipInfoCard}>
                    <div style={{ fontWeight: "bold", marginBottom: "6px" }}>
                      Live transport quote: ₹{summary.transportCharge}
                    </div>
                    <div style={mutedText}>
                      Route-aware pricing is active for this booking and adapts to
                      labour count, timing, and distance.
                    </div>
                  </div>
                ) : null}
              </div>
            )}

            {materialItems.length > 0 && (
              <div style={sectionCard}>
                <h2 style={sectionTitle}>Material Delivery</h2>
                <p style={sectionText}>
                  Materials do not use fixed delivery slots. Delivery estimate depends
                  on load, availability, and transport type.
                </p>

                <div style={toggleRow}>
                  <div>
                    <div style={{ fontWeight: "bold" }}>Priority delivery</div>
                    <div style={{ color: "#bdbdbd", marginTop: "4px" }}>
                      Faster estimated delivery at extra cost.
                    </div>
                  </div>

                  <button
                    onClick={() => setPriorityDelivery((prev) => !prev)}
                    style={{
                      ...toggleBtn,
                      background: priorityDelivery ? "#fff" : "#111",
                      color: priorityDelivery ? "#000" : "#fff",
                    }}
                  >
                    {priorityDelivery ? "Enabled" : "Disabled"}
                  </button>
                </div>

                <div style={{ marginTop: "18px", display: "grid", gap: "12px" }}>
                  {materialItems.map((item, index) => (
                    <div key={index} style={miniCard}>
                      <h4 style={{ marginTop: 0, marginBottom: "8px" }}>
                        {item.title}
                      </h4>
                      <p style={mutedText}>Quantity: {item.quantity || 1}</p>
                      <p style={mutedText}>
                        Estimated delivery:{" "}
                        {getMaterialDeliveryEstimate({
                          quantity: item.quantity || 1,
                          priorityDelivery,
                          distanceKm: item.distanceKm || 5,
                          vehicleType: item.vehicleType || "bike",
                        })}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div style={sectionCard}>
              <h2 style={sectionTitle}>Membership</h2>
              <p style={sectionText}>
                You can add, change, or remove your membership plan until the final
                payment step. Membership adds its upfront plan price now and gives
                10% cashback on eligible completed orders later. It does not reduce
                today&apos;s fare upfront.
              </p>

              <div style={membershipActionRow} className="checkout-page__membership-grid">
                <button
                  onClick={() => clearMembership()}
                  style={{
                    ...chip,
                    background: membership ? "#111" : "#fff",
                    color: membership ? "#fff" : "#000",
                  }}
                >
                  No Membership
                </button>

                {membershipPlans.map((plan) => {
                  const isSelected = membership?.name === plan.name;

                  return (
                    <button
                      key={plan.name}
                      onClick={() => setMembership(plan)}
                      style={{
                        ...membershipPlanChip,
                        background: isSelected ? "#fff" : "#111",
                        color: isSelected ? "#000" : "#fff",
                        border: isSelected
                          ? "1px solid #fff"
                          : "1px solid #2f2f2f",
                      }}
                    >
                      <div style={{ fontWeight: "bold" }}>{plan.name}</div>
                      <div style={{ fontSize: "12px", marginTop: "4px" }}>
                        ₹{plan.upfrontPrice} • 10% cashback
                      </div>
                    </button>
                  );
                })}
              </div>

              {membership ? (
                <div style={membershipInfoCard}>
                  <div style={{ fontWeight: "bold", marginBottom: "6px" }}>
                    Selected Plan: {membership.name}
                  </div>
                  <div style={mutedText}>
                    Tenure: {membership.duration} • Upfront membership price:
                    {" "}₹{membership.upfrontPrice}
                  </div>
                  <div style={mutedText}>
                    Cashback on this checkout after eligible completion: ₹
                    {summary.membershipCashback}
                  </div>
                </div>
              ) : (
                <div style={membershipInfoCard}>
                  <div style={{ fontWeight: "bold", marginBottom: "6px" }}>
                    No membership selected
                  </div>
                  <div style={mutedText}>
                    Add a plan now if you want checkout access to 10% cashback on
                    eligible completed orders.
                  </div>
                </div>
              )}
            </div>

            {hasProjectBriefServices ? (
              <div style={sectionCard}>
                <h2 style={sectionTitle}>Project Request Details</h2>
                <p style={sectionText}>
                  Professional and project-led services capture scope before execution so pricing, closures, and future commission rules stay measurable.
                </p>

                <div style={formGrid} className="checkout-page__form-grid">
                  <input
                    style={input}
                    placeholder="Budget Range"
                    value={projectBrief.budgetRange}
                    onChange={(e) =>
                      handleProjectBriefChange("budgetRange", e.target.value)
                    }
                  />
                  <input
                    style={input}
                    placeholder="Urgency"
                    value={projectBrief.urgency}
                    onChange={(e) =>
                      handleProjectBriefChange("urgency", e.target.value)
                    }
                  />
                  <input
                    style={input}
                    placeholder="Plot Size / Site Size"
                    value={projectBrief.plotSize}
                    onChange={(e) =>
                      handleProjectBriefChange("plotSize", e.target.value)
                    }
                  />
                  <input
                    style={input}
                    placeholder="File or image URLs (comma separated if available)"
                    value={projectBrief.attachmentUrls}
                    onChange={(e) =>
                      handleProjectBriefChange("attachmentUrls", e.target.value)
                    }
                  />
                </div>

                <textarea
                  style={{ ...input, minHeight: "120px", marginTop: "12px" }}
                  placeholder="Project details, use case, scope, constraints, or site notes"
                  value={projectBrief.projectDetails}
                  onChange={(e) =>
                    handleProjectBriefChange("projectDetails", e.target.value)
                  }
                />
              </div>
            ) : null}

            <div style={sectionCard}>
              <h2 style={sectionTitle}>Address</h2>
              <p style={sectionText}>
                Use your saved profile address or enter another address for this
                order. Selecting one of the two options below is mandatory before
                placing the order.
              </p>

              <div style={chipWrap} className="checkout-page__chip-wrap">
                <button
                  type="button"
                  onClick={() => setSelectedAddressType("profile")}
                  style={{
                    ...chip,
                    background: selectedAddressType === "profile" ? "#fff" : "#111",
                    color: selectedAddressType === "profile" ? "#000" : "#fff",
                  }}
                >
                  Use Profile Address
                </button>

                <button
                  type="button"
                  onClick={() => setSelectedAddressType("manual")}
                  style={{
                    ...chip,
                    background: selectedAddressType === "manual" ? "#fff" : "#111",
                    color: selectedAddressType === "manual" ? "#000" : "#fff",
                  }}
                >
                  Enter Manually
                </button>
              </div>

              {!selectedAddressType ? (
                <p style={warningText}>
                  Please choose an address mode before placing the order.
                </p>
              ) : null}

              <div style={formGrid} className="checkout-page__form-grid">
                <input
                  style={input}
                  placeholder="Flat No / House No"
                  value={addressForm.houseFlatNo}
                  onChange={(e) => handleAddressChange("houseFlatNo", e.target.value)}
                />
                <input
                  style={input}
                  placeholder="Street / Area Name"
                  value={addressForm.streetArea}
                  onChange={(e) => handleAddressChange("streetArea", e.target.value)}
                />
                <input
                  style={input}
                  placeholder="City"
                  value={addressForm.city}
                  onChange={(e) => handleAddressChange("city", e.target.value)}
                />
                <input
                  style={input}
                  placeholder="State"
                  value={addressForm.state}
                  onChange={(e) => handleAddressChange("state", e.target.value)}
                />
                <input
                  style={input}
                  placeholder="Country"
                  value={addressForm.country}
                  onChange={(e) => handleAddressChange("country", e.target.value)}
                />
                <input
                  style={input}
                  placeholder="Pincode"
                  value={addressForm.pincode}
                  onChange={(e) => handleAddressChange("pincode", e.target.value)}
                />
              </div>
            </div>
          </div>

          <div style={rightColumn}>
            <div style={summaryCard} className="checkout-page__summary-card">
              <h2 style={{ marginTop: 0 }}>Order Summary</h2>

              <div style={summaryRow}>
                <span>Service Subtotal</span>
                <span>₹{summary.serviceSubtotal}</span>
              </div>
              <div style={summaryRow}>
                <span>Mandatory Visitation Fee</span>
                <span>₹{summary.visitationFee}</span>
              </div>
              <div style={summaryRow}>
                <span>Materials Subtotal</span>
                <span>₹{summary.materialSubtotal}</span>
              </div>
              {summary.transportCharge > 0 ? (
                <div style={summaryRow}>
                  <span>Arranged Transport</span>
                  <span>₹{summary.transportCharge}</span>
                </div>
              ) : null}
              <div style={summaryRow}>
                <span>Service Fee</span>
                <span>₹{summary.serviceFee}</span>
              </div>
              <div style={summaryRow}>
                <span>Platform Fee</span>
                <span>₹{summary.platformFee}</span>
              </div>
              <div style={summaryRow}>
                <span>Delivery Charges</span>
                <span>₹{summary.deliveryCharge}</span>
              </div>
              <div style={summaryRow}>
                <span>Tax</span>
                <span>₹{summary.tax}</span>
              </div>
              <div style={summaryRow}>
                <span>Membership Plan Price</span>
                <span>₹{summary.membershipPrice}</span>
              </div>
              <div style={summaryRow}>
                <span>MB Credits After Completion</span>
                <span style={{ color: "#8dffb0" }}>
                  {summary.mbCreditsSuppressed
                    ? "Not available"
                    : `₹${summary.mbCreditsEarned}`}
                </span>
              </div>

              <hr style={divider} />

              <div style={billingSection}>
                <div style={billingSectionTitle}>Billing Items</div>
                {serviceItems.map((item, index) => {
                  const quantity = Number(item.quantity || 1);
                  const unitPrice = Number(item.basePrice || item.finalPrice || 0);
                  const itemVisitationFee =
                    unitPrice < 300 ? 99 * quantity : 0;

                  return (
                    <div key={`bill-service-${index}`} style={billingLine}>
                      <div>
                        <div>{item.title}</div>
                        <div style={billingMeta}>
                          Service • Qty {quantity}
                          {itemVisitationFee > 0
                            ? ` • Includes ₹${itemVisitationFee} visit fee`
                            : ""}
                        </div>
                      </div>
                      <div>₹{Number(item.finalPrice || 0) + itemVisitationFee}</div>
                    </div>
                  );
                })}

                {materialItems.map((item, index) => (
                  <div key={`bill-material-${index}`} style={billingLine}>
                    <div>
                      <div>{item.title}</div>
                      <div style={billingMeta}>
                        Material • Qty {Number(item.quantity || 1)}
                      </div>
                    </div>
                    <div>₹{Number(item.finalPrice || 0)}</div>
                  </div>
                ))}

                {summary.transportCharge > 0 ? (
                  <div style={billingLine}>
                    <div>
                      <div>MistriBazaar Arranged Transport</div>
                      <div style={billingMeta}>
                        Labour routing quote for {labourCount} worker
                        {labourCount > 1 ? "s" : ""}
                      </div>
                    </div>
                    <div>₹{summary.transportCharge}</div>
                  </div>
                ) : null}

                {membership ? (
                  <div style={billingLine}>
                    <div>
                      <div>Membership Plan - {membership.name}</div>
                      <div style={billingMeta}>
                        {membership.duration} • 10% cashback after eligible completion
                      </div>
                    </div>
                    <div>₹{summary.membershipPrice}</div>
                  </div>
                ) : null}
              </div>

              <div style={summaryTotalRow}>
                <span>Total</span>
                <span>₹{summary.total}</span>
              </div>

              {membership ? (
                <p style={summaryNote}>
                  Membership cashback is earned later on eligible completed orders.
                  It is not deducted from today&apos;s payable amount.
                </p>
              ) : null}

              <p style={{ ...summaryNote, color: "#8dffb0" }}>
                {summary.mbCreditsLabel}. Rewards apply only to labour charges and
                unlock after successful completion.
              </p>

              <button
                onClick={handleOnlinePayment}
                style={placeOrderBtn}
                disabled={placingOrder}
              >
                {placingOrder ? "Processing..." : "Pay Online"}
              </button>

              <button
                onClick={handleCashPayment}
                style={cashOrderBtn}
                disabled={placingOrder}
              >
                {placingOrder ? "Processing..." : "Cash on Delivery"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const page = {
  background: "#000",
  color: "#fff",
  minHeight: "100vh",
  padding: "24px 16px 80px",
};

const container = {
  maxWidth: "1240px",
  margin: "0 auto",
};

const backBtn = {
  marginBottom: "18px",
  padding: "10px 14px",
  border: "1px solid #2e2e2e",
  background: "#111",
  color: "#fff",
  borderRadius: "10px",
  cursor: "pointer",
};

const heroCard = {
  background: "linear-gradient(135deg, #111, #1a1a1a)",
  border: "1px solid #242424",
  borderRadius: "22px",
  padding: "24px",
  display: "flex",
  justifyContent: "space-between",
  gap: "16px",
  flexWrap: "wrap",
};

const heroTitle = {
  margin: 0,
  fontSize: "34px",
};

const heroText = {
  marginTop: "10px",
  color: "#bdbdbd",
  lineHeight: "1.7",
  maxWidth: "720px",
};

const heroTotalCard = {
  background: "#0d0d0d",
  border: "1px solid #252525",
  borderRadius: "18px",
  padding: "16px 18px",
  minWidth: "180px",
};

const heroTotalLabel = {
  fontSize: "13px",
  color: "#9d9d9d",
};

const heroTotalValue = {
  fontSize: "30px",
  fontWeight: "bold",
  marginTop: "8px",
};

const layout = {
  display: "grid",
  gridTemplateColumns: "1.4fr 0.85fr",
  gap: "20px",
  marginTop: "22px",
};

const leftColumn = {
  display: "grid",
  gap: "18px",
};

const rightColumn = {
  alignSelf: "start",
};

const sectionCard = {
  background: "linear-gradient(180deg, #111, #0b0b0b)",
  border: "1px solid #252525",
  borderRadius: "22px",
  padding: "22px",
};

const sectionTitle = {
  marginTop: 0,
  marginBottom: "8px",
};

const sectionText = {
  color: "#bdbdbd",
  lineHeight: "1.7",
  marginTop: 0,
};

const label = {
  display: "block",
  marginBottom: "10px",
  marginTop: "12px",
  fontWeight: "bold",
};

const chipWrap = {
  display: "flex",
  gap: "10px",
  flexWrap: "wrap",
};

const membershipActionRow = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
  gap: "12px",
  marginTop: "18px",
};

const chip = {
  padding: "10px 14px",
  border: "1px solid #303030",
  borderRadius: "999px",
  cursor: "pointer",
};

const transportChip = {
  ...chip,
  fontWeight: "bold",
};

const membershipPlanChip = {
  ...chip,
  borderRadius: "16px",
  padding: "14px",
  textAlign: "left",
};

const slotGrid = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
  gap: "10px",
};

const slotCard = {
  background: "#101010",
  color: "#fff",
  borderRadius: "14px",
  padding: "12px",
  textAlign: "left",
  cursor: "pointer",
};

const slotStatus = (status) => ({
  marginTop: "6px",
  fontSize: "13px",
  color:
    status === "available"
      ? "#8dffb0"
      : status === "filling_fast"
      ? "#ffd37b"
      : "#ff8f8f",
});

const toggleRow = {
  display: "flex",
  justifyContent: "space-between",
  gap: "12px",
  alignItems: "center",
  flexWrap: "wrap",
  background: "#0f0f0f",
  border: "1px solid #252525",
  borderRadius: "16px",
  padding: "14px",
  marginTop: "16px",
};

const toggleBtn = {
  padding: "10px 16px",
  border: "1px solid #2f2f2f",
  borderRadius: "12px",
  cursor: "pointer",
  fontWeight: "bold",
};

const miniCard = {
  background: "#0f0f0f",
  border: "1px solid #242424",
  borderRadius: "16px",
  padding: "14px",
};

const mutedText = {
  color: "#bdbdbd",
  margin: "4px 0",
};

const warningText = {
  color: "#ffd37b",
  marginTop: "12px",
  marginBottom: 0,
  fontSize: "13px",
  lineHeight: "1.6",
};

const membershipInfoCard = {
  marginTop: "16px",
  background: "#0f0f0f",
  border: "1px solid #252525",
  borderRadius: "16px",
  padding: "14px",
};

const formGrid = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
  gap: "12px",
  marginTop: "18px",
};

const input = {
  padding: "12px 14px",
  borderRadius: "12px",
  border: "1px solid #303030",
  background: "#0c0c0c",
  color: "#fff",
  outline: "none",
};

const summaryCard = {
  background: "linear-gradient(180deg, #141414, #0f0f0f)",
  border: "1px solid #252525",
  borderRadius: "22px",
  padding: "22px",
  position: "sticky",
  top: "90px",
};

const summaryRow = {
  display: "flex",
  justifyContent: "space-between",
  gap: "12px",
  margin: "12px 0",
  color: "#d7d7d7",
};

const divider = {
  borderColor: "#262626",
  margin: "18px 0",
};

const billingSection = {
  marginTop: "18px",
  marginBottom: "18px",
  display: "grid",
  gap: "12px",
};

const billingSectionTitle = {
  fontSize: "14px",
  fontWeight: "bold",
  color: "#f3f3f3",
  textTransform: "uppercase",
  letterSpacing: "0.08em",
};

const billingLine = {
  display: "flex",
  justifyContent: "space-between",
  gap: "12px",
  alignItems: "flex-start",
  color: "#e4e4e4",
};

const billingMeta = {
  color: "#9f9f9f",
  fontSize: "12px",
  marginTop: "4px",
  lineHeight: "1.6",
};

const summaryTotalRow = {
  display: "flex",
  justifyContent: "space-between",
  gap: "12px",
  fontSize: "24px",
  fontWeight: "bold",
};

const summaryNote = {
  color: "#bdbdbd",
  lineHeight: "1.7",
  marginTop: "14px",
  marginBottom: 0,
  fontSize: "13px",
};

const placeOrderBtn = {
  marginTop: "20px",
  width: "100%",
  padding: "14px 18px",
  border: "none",
  background: "#fff",
  color: "#000",
  borderRadius: "14px",
  cursor: "pointer",
  fontWeight: "bold",
  fontSize: "16px",
};

const cashOrderBtn = {
  ...placeOrderBtn,
  marginTop: "12px",
  background: "#151515",
  color: "#ffffff",
  border: "1px solid #303030",
};

export default Checkout;
